import { useCallback, useContext, useState } from "react";
import { TiltifyConfigContext } from "@/context/tiltifyConfigContext.js";
import { isTestMode } from "@/util/const.js";
import { type ChangeEventLike, restoreFromLocalStorage } from "@/util/localStorageConfig.ts";

export const defaultTiltifyConfig = {
  tiltifyUserID: isTestMode ? "tiltify-test-user" : "",
  tiltifyUsername: isTestMode ? "test-user" : "",
  tiltifyUserSlug: isTestMode ? "test-user" : "",
  tiltifyCampaignSlug: isTestMode ? "test-campaign" : "",
  tiltifyCampaignID: isTestMode ? "test-campaign-id" : "",
  tiltifyPollID: isTestMode ? "test-poll-id" : "",
  tiltifyCampaignGoal: isTestMode ? "1000" : "0",
  tiltifyCampaignName: isTestMode ? "Test Campaign" : "",
  tiltifyTeamSlug: "",
  tiltifyIsTeamCampaign: false as boolean,
  tiltifyAllowRetired: false as boolean,
  tiltifyAccessToken: "",
  tiltifyRefreshToken: "",
  tiltifyTokenExpiresIn: "",
  tiltifyTokenCreatedAt: "",
};

export type TiltifyConfig = typeof defaultTiltifyConfig;

/** Hook that reads from the shared TiltifyConfigContext. */
export default function useTiltifyConfig() {
  return useContext(TiltifyConfigContext);
}

/** Returns the state and setters needed to back TiltifyConfigContext — used by TiltifyConfigProvider. */
export function useTiltifyConfigState() {
  const [config, setConfig] = useState<TiltifyConfig>(() =>
    restoreFromLocalStorage(defaultTiltifyConfig),
  );

  const setField = useCallback(<K extends keyof TiltifyConfig>(key: K, value: TiltifyConfig[K]) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
    localStorage.setItem(key, String(value));
  }, []);

  const setFromEvent = useCallback((e: ChangeEventLike) => {
    const { name, value } = e.target;
    setConfig((prev) => ({ ...prev, [name]: value }));
    localStorage.setItem(name, value);
  }, []);

  const clearField = useCallback((key: keyof TiltifyConfig) => {
    setConfig((prev) => ({ ...prev, [key]: defaultTiltifyConfig[key] }));
    localStorage.removeItem(key);
  }, []);

  return { config, setField, setFromEvent, clearField };
}
